import React from 'react';
import Icon from 'components/AppIcon';

export default function AssessmentResults({ selectedChemical, selectedMaterial, scenarioData }) {
  const assessmentResults = {
    overallRating: 'B',
    riskLevel: 'Moderate',
    compatibilityScore: 78,
    recommendations: [
      'Regular inspection recommended every 6 months',
      'Monitor for signs of degradation',
      'Consider alternative materials for long-term storage',
    ],
    testData: [
      { parameter: 'Corrosion Rate', value: '0.05 mm/year', status: 'acceptable', color: 'text-green-500' },
      { parameter: 'Material Strength', value: '95% retained', status: 'good', color: 'text-green-500' },
      { parameter: 'Surface Degradation', value: 'Minimal', status: 'acceptable', color: 'text-yellow-500' },
      { parameter: 'Chemical Resistance', value: 'Good', status: 'good', color: 'text-green-500' },
    ],
    reactionPotential: 'Low',
    safetyFactors: {
      temperature: 'Within safe limits',
      pressure: 'Acceptable range',
      duration: 'Suitable for application',
    },
  };

  return (
    <div className="space-y-6">
      {/* Overall Assessment */}
      <div className="card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Assessment Results</h3>
            <p className="text-sm text-muted-foreground">
              Based on scenario conditions: {scenarioData?.temperature}°C, {scenarioData?.pressure} bar
            </p>
          </div>
          <button className="btn-primary flex items-center gap-2">
            <Icon name="Download" size={18} />
            Export Report
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 bg-blue-500/10 rounded-lg border-2 border-blue-500">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-500 mb-2">
                {assessmentResults?.overallRating}
              </div>
              <div className="font-medium text-foreground mb-1">Overall Rating</div>
              <div className="text-sm text-muted-foreground">Good Compatibility</div>
            </div>
          </div>

          <div className="p-6 bg-yellow-500/10 rounded-lg">
            <div className="text-center">
              <Icon name="AlertTriangle" size={32} className="text-yellow-500 mx-auto mb-2" />
              <div className="font-medium text-foreground mb-1">Risk Level</div>
              <div className="text-lg font-semibold text-foreground">{assessmentResults?.riskLevel}</div>
            </div>
          </div>

          <div className="p-6 bg-green-500/10 rounded-lg">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-500 mb-2">
                {assessmentResults?.compatibilityScore}%
              </div>
              <div className="font-medium text-foreground mb-1">Compatibility Score</div>
              <div className="w-full bg-border rounded-full h-2 mt-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${assessmentResults?.compatibilityScore}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Test Data */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Detailed Test Results</h3>
        <div className="space-y-3">
          {assessmentResults?.testData?.map((test, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-accent/50 rounded-lg">
              <div className="flex items-center gap-3 flex-1">
                <Icon name="Activity" size={20} className="text-primary" />
                <div>
                  <div className="font-medium text-foreground">{test?.parameter}</div>
                  <div className={`text-sm ${test?.color}`}>{test?.status?.toUpperCase()}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-foreground">{test?.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Safety Recommendations */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="ShieldCheck" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Safety Recommendations</h3>
            <p className="text-sm text-muted-foreground">Important guidelines for safe use</p>
          </div>
        </div>
        <ul className="space-y-3">
          {assessmentResults?.recommendations?.map((rec, index) => (
            <li key={index} className="flex items-start gap-3 p-3 bg-accent/50 rounded-lg">
              <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-foreground">{rec}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* Reaction Potential */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Icon name="Zap" size={24} className="text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Reaction Potential</h3>
              <p className="text-2xl font-bold text-green-500">{assessmentResults?.reactionPotential}</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            No significant chemical reactions expected under specified conditions
          </p>
        </div>

        <div className="card">
          <h3 className="font-semibold text-foreground mb-4">Safety Factors</h3>
          <div className="space-y-2">
            {Object.entries(assessmentResults?.safetyFactors)?.map(([key, value]) => (
              <div key={key} className="flex items-center justify-between p-2 bg-accent/50 rounded">
                <span className="text-sm text-muted-foreground capitalize">{key}</span>
                <span className="text-sm font-medium text-foreground">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Export Options */}
      <div className="card bg-accent/50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground mb-1">Export Assessment</h3>
            <p className="text-sm text-muted-foreground">
              Generate detailed report for compliance and documentation
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="FileText" size={18} />
              PDF Report
            </button>
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="Table" size={18} />
              Excel Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}